import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Upload } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ProcessingQueue } from '@/components/overview/ProcessingQueue'
import { DomainCard } from '@/components/overview/DomainCard'
import { GapCard } from '@/components/overview/GapCard'
import { SourceItem } from '@/components/overview/SourceItem'
import { documentService, contextService, ApiError } from '@/services'
import { useUpdatesStream } from '@/hooks/useUpdatesStream'
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback'
import { useAppStore } from '@/stores/appStore'
import {
  DOMAINS,
  completenessFromScore,
  countFilledDomains,
  deriveCompleteness,
  isFieldFilled,
} from '@/lib/domainSchema'
import type {
  ContextGap,
  Document,
  DomainScore,
  StructuredBusinessContext,
} from '@/types/api'

/**
 * The default landing once a startup exists. Five sections stacked under a
 * single page-title block: upload → processing queue → domain grid → gap
 * list → sources. SSE drives the live invalidation: any document.* event
 * refetches sources + source-of-truth + gaps; any gap.* event refetches
 * gaps + updates the sidebar badge counter.
 *
 * State is local to this page intentionally — no Zustand slice for
 * documents / gaps / SoT, because the canonical query is cheap and SSE
 * fan-out keeps the UI honest. Caching here would just mean a stale-
 * window after every event.
 */
export function OverviewPage() {
  const navigate = useNavigate()
  const startupId = useAppStore((s) => s.startup?.id ?? null)
  const setOpenGapCount = useAppStore((s) => s.setOpenGapCount)

  const [sourceOfTruth, setSourceOfTruth] = useState<StructuredBusinessContext>({})
  const [gaps, setGaps] = useState<ContextGap[]>([])
  const [domainScores, setDomainScores] = useState<DomainScore[]>([])
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Refs over setters so the SSE refetch closure stays stable.
  const startupIdRef = useRef(startupId)
  startupIdRef.current = startupId

  const fetchAll = useCallback(async () => {
    if (!startupId) return
    setLoading(true)
    setError(null)
    // Render whichever sections resolve. One failed fetch shouldn't blank
    // the page — past learning `promise-all-wrong-primitive`.
    const [sotR, gapR, docsR] = await Promise.allSettled([
      contextService.getSourceOfTruth(startupId),
      contextService.getGaps(startupId, { status: 'open' }),
      documentService.list(startupId),
    ])
    if (sotR.status === 'fulfilled') {
      setSourceOfTruth(sotR.value)
    }
    if (gapR.status === 'fulfilled') {
      setGaps(gapR.value.gaps ?? [])
      setDomainScores(gapR.value.domainScores ?? [])
      setOpenGapCount(gapR.value.gapsFound ?? gapR.value.gaps?.length ?? 0)
    }
    if (docsR.status === 'fulfilled') {
      setDocuments(docsR.value.documents ?? [])
    }
    // Surface a banner only if ALL three failed AND none was a 401.
    const failures = [sotR, gapR, docsR].filter(
      (r): r is PromiseRejectedResult => r.status === 'rejected'
    )
    const has401 = failures.some(
      (f) => f.reason instanceof ApiError && f.reason.status === 401
    )
    if (failures.length === 3 && !has401) {
      const first = failures[0].reason
      setError(first instanceof Error ? first.message : 'Failed to load overview.')
    }
    setLoading(false)
  }, [startupId, setOpenGapCount])

  useEffect(() => {
    void fetchAll()
  }, [fetchAll])

  // Invalidate on every document/gap/context event for this startup.
  // Debounce coalesces SSE bursts (ingestion can fire 5-10 events in <1s)
  // into a single refetch fanout.
  const debouncedRefetch = useDebouncedCallback(() => {
    void fetchAll()
  }, 250)
  useUpdatesStream({
    startupId,
    filters: { entityTypes: ['document', 'gap', 'context'] },
    enabled: !!startupId,
    onUpdate: debouncedRefetch,
  })

  const inflightDocs = useMemo(
    () =>
      documents.filter(
        (d) =>
          d.status === 'uploading' ||
          d.status === 'pending' ||
          d.status === 'processing'
      ),
    [documents]
  )

  // The analyzer's per-domain score is the better signal: it weighs coverage,
  // confidence and freshness. Fall back to counting populated fields when the
  // startup has not been scored yet.
  const scoreByDomain = useMemo(
    () => new Map(domainScores.map((d) => [d.domain as string, d.score])),
    [domainScores]
  )

  const hasAnyContent =
    documents.length > 0 ||
    Object.values(sourceOfTruth).some((v) => v && Object.keys(v).length > 0)

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col gap-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-text-primary tracking-tight">
            Context Engine
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            Everything we know about your business. Upload anything, fill the
            gaps, keep your source of truth current.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => navigate('/context/add?mode=upload')}
          className="gap-1.5 bg-accent text-text-on-accent hover:bg-accent/90"
        >
          <Upload className="h-3.5 w-3.5" />
          Upload
        </Button>
      </header>

      <ProcessingQueue documents={inflightDocs} />

      {error && (
        <div
          role="alert"
          className="text-sm px-3 py-2 rounded-md border border-warning/30 bg-warning/10 text-text-primary"
        >
          {error}
        </div>
      )}

      <Section
        title="What we know"
        meta={
          loading
            ? null
            : countFilledDomains(sourceOfTruth) > 0
              ? `${countFilledDomains(sourceOfTruth)} of ${DOMAINS.length} domains have content`
              : null
        }
      >
        {!hasAnyContent ? (
          <EmptyOverviewHero />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {DOMAINS.map((d) => {
              const section = (sourceOfTruth[d.key] ?? {}) as Record<
                string,
                unknown
              >
              const score = scoreByDomain.get(d.key as string)
              const completeness =
                score === undefined
                  ? deriveCompleteness(section)
                  : completenessFromScore(score)
              return (
                <DomainCard
                  key={d.key as string}
                  domainKey={d.key as string}
                  label={d.label}
                  completeness={completeness}
                  summary={summarize(section)}
                  to={d.href}
                />
              )
            })}
          </div>
        )}
      </Section>

      <Section
        title="What we don't know yet"
        meta={
          gaps.length > 0
            ? `${gaps.length} open gap${gaps.length === 1 ? '' : 's'}`
            : null
        }
      >
        {gaps.length === 0 ? (
          <p className="text-sm text-text-secondary px-1">
            We’ll surface gaps after your first upload.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {gaps.map((g) => (
              <GapCard key={g.id} gap={g} />
            ))}
          </div>
        )}
      </Section>

      {documents.length > 0 && (
        <Section
          title="Sources"
          meta={`${documents.length} document${documents.length === 1 ? '' : 's'}`}
        >
          <ul className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
            {documents.map((doc) => (
              <SourceItem key={doc.id} document={doc} />
            ))}
          </ul>
        </Section>
      )}
    </div>
  )
}

interface SectionProps {
  title: string
  meta?: string | null
  children: React.ReactNode
}

function Section({ title, meta, children }: SectionProps) {
  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-end justify-between gap-3">
        <h2 className="text-lg font-display font-semibold text-text-primary">
          {title}
        </h2>
        {meta && <span className="text-xs text-text-tertiary">{meta}</span>}
      </header>
      {children}
    </section>
  )
}

function EmptyOverviewHero() {
  return (
    <div className="rounded-lg border border-border-default bg-bg-primary px-6 py-10 text-center">
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary mb-3">
        <Sparkles size={18} />
      </div>
      <h3 className="text-base font-display font-semibold text-text-primary">
        No context yet.
      </h3>
      <p className="mt-1 text-sm text-text-secondary max-w-md mx-auto">
        Upload a doc above and we’ll start building your context engine across
        eight business domains.
      </p>
    </div>
  )
}

function summarize(section: Record<string, unknown>): string | null {
  const filled = Object.entries(section ?? {})
    .filter(([, v]) => isFieldFilled(v))
    .map(([, v]) => valueToString(v))
    .filter((s): s is string => s !== null)
  if (filled.length === 0) return null
  // Compose a short summary from the first 1-2 filled values.
  return filled.slice(0, 2).join(' · ')
}

function valueToString(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null
  if (Array.isArray(value)) {
    const flat = value.filter((x): x is string => typeof x === 'string').join(', ')
    return flat || null
  }
  if (value && typeof value === 'object') {
    const firstString = Object.values(value as object).find(
      (v): v is string => typeof v === 'string' && v.trim().length > 0
    )
    return firstString ?? null
  }
  return null
}
